import { useEffect, useRef, useState } from "react";
import { LeafLearnMoreLink } from "../../leaf/LeafLearnMoreLink/LeafLearnMoreLink";
import { TimeStamp } from "../TimeStamp/TimeStamp";
import { useAppSelector } from "../../../store";
import { NewPeriodSelector } from "../../../components/Navigation/NewPeriodSelector/NewPeriodSelector";
import { usePeriodSelectorVisibility } from "../../hooks/usePeriodSelectorVisibility";
import breakpoints from "../../../common/scss/breakpoints.module.scss";
import "./PageTitleSection.scss";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { ROUTE_PATHS } from "../../enums/routing-enums";
import useWindowSize from "../../hooks/useWindowSize";

type Props = {
  title: React.ReactNode;
  infoButton?: React.ReactNode;
  hideTimeStamp?: boolean;
  learnMoreLink?: string;
  learnMoreTealiumFunction?: () => void;
  dateFormat?: "long" | "short" | "bigEndian";
  hidePeriodSelector?: boolean;
  actionReportSelector?: React.ReactNode;
  className?: string;
};

const OVERRIDE_MARGIN: Set<string> = new Set([
  ROUTE_PATHS.SSI_UPLINE_REPORT,
  ROUTE_PATHS.MBIMonthlyBonuses,
  ROUTE_PATHS.CUSTOMER_LIST,
]);
const EXCLUDE_PORTAL_PATHS: Set<string> = new Set([]);

export const PageTitleSection = ({
  title,
  infoButton,
  hideTimeStamp = false,
  learnMoreLink,
  learnMoreTealiumFunction,
  dateFormat = "bigEndian",
  hidePeriodSelector,
  actionReportSelector,
  className,
}: Props) => {
  const { deviceSize } = useAppSelector((state) => state.loginLocalStorage);
  const { width } = useWindowSize();
  const displayPeriodSelector = usePeriodSelectorVisibility();
  const isPeriodSelectorHidden =
    hidePeriodSelector ||
    !displayPeriodSelector ||
    (width !== undefined && Number(width) >= Number(breakpoints.desktop));
  const isMobile = deviceSize === "mobile";
  const { pathname } = useLocation();
  const titleRowRef = useRef<HTMLDivElement>(null);
  const [isTitleRowWrapped, setIsTitleRowWrapped] = useState(false);

  useEffect(() => {
    const titleRow = titleRowRef.current;
    if (!titleRow || !learnMoreLink) return;

    const checkWrap = () => {
      const children = Array.from(titleRow.children) as HTMLElement[];
      if (children.length >= 2) {
        setIsTitleRowWrapped(children[0].getBoundingClientRect().top < children[1].getBoundingClientRect().top);
      }
    };

    const observer = new ResizeObserver(checkWrap);
    observer.observe(titleRow);
    checkWrap();
    return () => observer.disconnect();
  }, [learnMoreLink]);

  const learnMoreLinkElement = learnMoreLink && (
    <div className="PageTitleSection--learnMoreLink">
      <LeafLearnMoreLink
        href={learnMoreLink}
        onClickFn={learnMoreTealiumFunction}
        removeBorder={true}
        underline={true}
        theme={"blue"}
      />
    </div>
  );

  const overrideMarginFlag = OVERRIDE_MARGIN.has(pathname);

  const marginFixFlag =
    pathname === ROUTE_PATHS.PERFORMANCE_DASHBOARD || pathname === ROUTE_PATHS.GROUP_MANAGEMENT_DASHBOARD;
  const marginRevertFlag = pathname === ROUTE_PATHS.EDLOS;
  const titleCard = (
    <>
      <div
        className={`
        PageTitleSection--wrapper 
        ${marginFixFlag ? "PageTitleSection--wrapper--gap-small" : marginRevertFlag ? "PageTitleSection--wrapper--gap-revert" : "PageTitleSection--wrapper--gap-large"} 
        ${overrideMarginFlag ? "PageTitleSection--wrapper--gap-large--margin-override" : ""}
      `}
      >
        <div className="PageTitleSection--header">
          <span className="PageTitleSection--header-container">
            <h1 className="PageTitleSection--title" id="title-section-header">
              {actionReportSelector || title}
            </h1>
            {infoButton}
          </span>
          {learnMoreLinkElement}
        </div>
        {!hideTimeStamp && (
          <div className="PageTitleSection--timestamp">
            <TimeStamp dateFormat={dateFormat} />
          </div>
        )}
      </div>
    </>
  );

  const [portalTarget, setPortalTarget] = useState<Element | null>(null);

  useEffect(() => {
    setPortalTarget(document.querySelector("#title-portal"));
  }, [width]);
  const portalFlag = portalTarget !== null;

  if (portalFlag && portalTarget && !EXCLUDE_PORTAL_PATHS.has(pathname)) {
    return createPortal(titleCard, portalTarget);
  }

  return (
    <div className={`PageTitleSection ${className ? className : ""}`}>
      {isMobile ? (
        <>
          <div className="PageTitleSection--top">
            <div
              className={`PageTitleSection--header-left${isTitleRowWrapped ? " PageTitleSection--header-left--wrapped" : ""}`}
            >
              <div className="PageTitleSection--title-row" ref={titleRowRef}>
                <span className="PageTitleSection--header-container">
                  <h1
                    className={`PageTitleSection--title ${pathname === ROUTE_PATHS.MONTHLY_Q ? "PageTitleSection--title__sizing-fix" : ""}`}
                    id="title-section-header"
                  >
                    {actionReportSelector || title}
                  </h1>
                  {infoButton}
                </span>
              </div>
              {!isPeriodSelectorHidden && (
                <div className="PageTitleSection--periodSelector">
                  <NewPeriodSelector />
                </div>
              )}
            </div>
          </div>
          {!hideTimeStamp && (
            <div className="PageTitleSection--timestamp">
              <TimeStamp dateFormat={dateFormat} />
            </div>
          )}
          {learnMoreLinkElement}
        </>
      ) : (
        <>
          <div className="PageTitleSection--top">
            <div
              className={`PageTitleSection--header-left${isTitleRowWrapped ? " PageTitleSection--header-left--wrapped" : ""}`}
            >
              <div className="PageTitleSection--title-row" ref={titleRowRef}>
                <span className="PageTitleSection--header-container">
                  <h1 className="PageTitleSection--title" id="title-section-header">
                    {actionReportSelector || title}
                  </h1>
                  {infoButton}
                </span>
                {learnMoreLinkElement}
              </div>
              {!isPeriodSelectorHidden && (
                <div className="PageTitleSection--periodSelector">
                  <NewPeriodSelector />
                </div>
              )}
            </div>
          </div>
          {!hideTimeStamp && (
            <div className="PageTitleSection--timestamp">
              <TimeStamp dateFormat={dateFormat} />
            </div>
          )}
        </>
      )}
    </div>
  );
};
